import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './ChooseTemplateModal.module.scss';
import {
  Button,
  Link,
  Loading,
  Modal,
  ModalBody,
  ModalHeader,
  Text,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { fetchReviewsWithCopyableTemplates, getReviewId } from 'query/review';
import { useCurrentReviewer } from 'hooks/reviewers/useCurrentReviewer';
import { CopyableReviewList } from 'components/shared/CopyableReviewList/CopyableReviewList';
import {
  DATA_EXTRACTION_TEMPLATE_CREATE_OR_EDIT_ROUTE,
  tokenizeRoute,
} from 'query/routes';

const KNOWLEDGE_BASE_COPY_TEMPLATE_LINK =
  'https://support.covidence.org/help/copy-template-from-another-review';

type Props = {
  isModalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ChooseTemplateModal = ({ isModalOpen, setModalOpen }: Props) => {
  const reviewId = getReviewId();
  if (!reviewId) throw new Error("Couldn't get Review ID.");

  const { currentReviewer, isLoading } = useCurrentReviewer();
  const { isFetching, data: copyableReviewTemplates, error } = useQuery({
    queryKey: ['copyableReviewTemplates', currentReviewer?.id],
    queryFn: () => fetchReviewsWithCopyableTemplates(reviewId),
    staleTime: 300_000,
    select: (response) => response.source_reviews,
    enabled: !isLoading,
  });

  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  );
  const [isCopying, setIsCopying] = useState(false);

  const onCopyTemplate = async () => {
    if (!selectedReviewId) {
      setValidationError('Select a review to copy template');
      return;
    }

    setIsCopying(true);
    setValidationError(undefined);
    const currentParams = new URLSearchParams(window.location.search);
    const url = tokenizeRoute(DATA_EXTRACTION_TEMPLATE_CREATE_OR_EDIT_ROUTE, {
      review_id: reviewId,
      source_review_id: selectedReviewId,
      ...(currentParams.get('backTo') && {
        backTo: currentParams.get('backTo'),
      }),
      ...(currentParams.get('type') && { type: currentParams.get('type') }),
    });
    window.location.href = url.toString();
  };

  const onSelectReview = (revId: number) => {
    setSelectedReviewId(revId);
    setValidationError(undefined);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setModalOpen(false)}
      className={styles.CopyModal}
    >
      <ModalHeader>Select a review to copy template</ModalHeader>
      <ModalBody>
        <Stack spacing={3}>
          <Text className={styles.CopyModalInstructions}>
            Your current template will be replaced with a{' '}
            <Link
              variant="primary"
              external={true}
              to={KNOWLEDGE_BASE_COPY_TEMPLATE_LINK}
            >
              {"copy of the selected review's template."}
            </Link>
            <br />
            Only published Extraction 1 templates can be copied.
          </Text>
          {isFetching && (
            <Inline>
              <Loading size="xl" />
            </Inline>
          )}
          {error && (
            <Text size="md" variant="danger">
              Failed to load reviews. Please try again.
            </Text>
          )}
          {copyableReviewTemplates !== undefined && (
            <CopyableReviewList
              reviews={copyableReviewTemplates ?? []}
              onChange={(reviewId) => onSelectReview(reviewId)}
              error={validationError}
              aria-label="Select a review to copy template from"
            />
          )}
          <Inline alignX="right" spacing={3}>
            <Button
              type="neutral"
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={isCopying}
              htmlType="button"
            >
              Cancel
            </Button>
            <Button
              type="brand"
              onClick={onCopyTemplate}
              disabled={isCopying}
              isLoading={isCopying}
              data-pendo-key="de1-reuse-template-copy"
            >
              Copy template
            </Button>
          </Inline>
        </Stack>
      </ModalBody>
    </Modal>
  );
};
